(() => {
  const drawer = document.getElementById('wb-quick-add-drawer');
  const overlay = document.querySelector('.wb-quick-add__overlay');
  if (!drawer || !overlay) return;

  const els = {
    title: drawer.querySelector('[data-wb-title]'),
    price: drawer.querySelector('[data-wb-price]'),
    image: drawer.querySelector('[data-wb-image]'),
    options: drawer.querySelector('[data-wb-options]'),
    purchase: drawer.querySelector('[data-wb-purchase]'),
    subscribePrice: drawer.querySelector('[data-wb-subscribe-price]'),
    oncePrice: drawer.querySelector('[data-wb-once-price]'),
    frequency: drawer.querySelector('[data-wb-frequency]'),
    qty: drawer.querySelector('[data-wb-qty-value]'),
    subtotal: drawer.querySelector('[data-wb-subtotal]'),
    submit: drawer.querySelector('[data-wb-submit]'),
    details: drawer.querySelector('[data-wb-details]'),
    error: drawer.querySelector('[data-wb-error]'),
  };

  const state = {
    product: null,
    variant: null,
    selectedOptions: [],
    purchase: 'subscribe',
    selectedPlanId: null,
    qty: 1,
    trigger: null,
    productHandle: null,
  };

  const sealNoteCache = new Map();
  const FALLBACK_SUBSCRIBE_NOTE =
    '25% off your first three subscriptions, then 15% off after. Not stackable with other discounts.';
  let giftPlanIds = new Set();

  const moneyFormat = (cents) => {
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      const format = window.Shopify.money_format || '${{amount}}';
      return window.Shopify.formatMoney(cents, format);
    }
    const currency =
      (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) ||
      (window.Shopify && window.Shopify.currency) ||
      'GBP';
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency,
    }).format(cents / 100);
  };

  const normalize = (value) => (value || '').toString().trim().toLowerCase();

  const getProductJson = async (handle) => {
    const response = await fetch(`/products/${handle}.js`);
    if (!response.ok) throw new Error('Unable to load product');
    return response.json();
  };

  const setError = (message) => {
    els.error.textContent = message || '';
  };

  const clearError = () => setError('');

  const getSellingPlansForVariant = (product, variant) => {
    if (!product || !variant) return [];
    const allocations = Array.isArray(variant.selling_plan_allocations) ? variant.selling_plan_allocations : [];
    const allowed = new Set(allocations.map((item) => item.selling_plan_id));
    if (!allowed.size) return [];
    const plans = [];
    if (Array.isArray(product.selling_plan_groups)) {
      product.selling_plan_groups.forEach((group) => {
        const groupName = group?.name || '';
        if (Array.isArray(group.selling_plans)) {
          group.selling_plans.forEach((plan) => {
            if (allowed.has(plan.id)) {
              plans.push({ ...plan, groupName });
            }
          });
        }
      });
    }
    return plans;
  };

  const getDefaultVariant = (product) => {
    return product.variants.find((variant) => variant.available) || product.variants[0];
  };

  const normalizeOptionValue = (value) => {
    if (typeof value === 'string') return value;
    if (value && typeof value === 'object') {
      return value.value || value.name || value.label || '';
    }
    if (value == null) return '';
    return String(value);
  };

  const normalizeOptionName = (value) => normalizeOptionValue(value) || 'Option';

  const getOptionValues = (product, optionIndex) => {
    const values = new Set();
    product.variants.forEach((variant) => {
      const value = normalizeOptionValue(variant[`option${optionIndex + 1}`]);
      if (value) values.add(value);
    });
    return Array.from(values);
  };

  const buildOptions = () => {
    const product = state.product;
    if (!product) return;
    els.options.innerHTML = '';

    const optionGroups = Array.isArray(product.options_with_values) && product.options_with_values.length
      ? product.options_with_values.map((option, index) => ({
          index,
          name: normalizeOptionName(option.name),
          values: Array.isArray(option.values) ? option.values.map(normalizeOptionValue).filter(Boolean) : [],
        }))
      : product.options.map((optionName, index) => ({
          index,
          name: normalizeOptionName(optionName),
          values: getOptionValues(product, index),
        }));

    optionGroups.forEach((groupData) => {
      const values = groupData.values;
      if (!values.length) return;

      const group = document.createElement('div');
      group.className = 'wb-quick-add__group';

      const label = document.createElement('div');
      label.className = 'wb-quick-add__label';
      label.textContent = groupData.name;

      const grid = document.createElement('div');
      grid.className = 'wb-quick-add__option-grid';
      grid.dataset.optionIndex = String(groupData.index);

      values.forEach((value) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'wb-quick-add__option';
        button.textContent = value;
        button.dataset.value = value;
        if (state.selectedOptions[groupData.index] === value) {
          button.classList.add('is-selected');
        }
        grid.appendChild(button);
      });

      group.appendChild(label);
      group.appendChild(grid);
      els.options.appendChild(group);
    });
  };

  const findVariantMatch = (selection) => {
    const product = state.product;
    if (!product) return null;
    return product.variants.find((variant) => {
      return selection.every((value, index) => !value || variant[`option${index + 1}`] === value);
    });
  };

  const resolveVariantAfterSelection = (selectedIndex) => {
    const product = state.product;
    if (!product) return null;
    const selection = [...state.selectedOptions];
    const exactMatch = findVariantMatch(selection);
    if (exactMatch) return exactMatch;

    const selectedValue = selection[selectedIndex];
    const candidates = product.variants.filter((variant) => variant[`option${selectedIndex + 1}`] === selectedValue);
    if (!candidates.length) return null;
    return candidates.find((variant) => variant.available) || candidates[0];
  };

  const updateOptionAvailability = () => {
    const product = state.product;
    if (!product) return;

    drawer.querySelectorAll('.wb-quick-add__option-grid').forEach((grid) => {
      const optionIndex = Number(grid.dataset.optionIndex);
      grid.querySelectorAll('.wb-quick-add__option').forEach((button) => {
        const value = button.dataset.value;
        const isCompatible = product.variants.some((variant) => {
          if (variant[`option${optionIndex + 1}`] !== value) return false;
          return state.selectedOptions.every((selected, index) => {
            if (!selected || index === optionIndex) return true;
            return variant[`option${index + 1}`] === selected;
          });
        });
        button.classList.toggle('is-disabled', !isCompatible);
        button.disabled = !isCompatible;
        button.setAttribute('aria-disabled', !isCompatible ? 'true' : 'false');
      });
    });
  };

  const updateOptionButtons = () => {
    drawer.querySelectorAll('.wb-quick-add__option-grid').forEach((grid) => {
      const index = Number(grid.dataset.optionIndex);
      grid.querySelectorAll('.wb-quick-add__option').forEach((button) => {
        const isSelected = state.selectedOptions[index] === button.dataset.value;
        button.classList.toggle('is-selected', isSelected);
      });
    });
    updateOptionAvailability();
  };

  const buildPurchase = () => {
    const product = state.product;
    if (!product) return;

    const purchaseGroup = els.purchase.closest('.wb-quick-add__group');
    const rawPlans = getSellingPlansForVariant(product, state.variant);
    const hasPlans = rawPlans.length > 0;
    const requiresPlan = state.variant && state.variant.requires_selling_plan;

    els.purchase.classList.toggle('wb-has-plans', hasPlans);

    if (!hasPlans) {
      els.purchase.innerHTML = '';
      if (purchaseGroup) purchaseGroup.style.display = 'none';
      state.purchase = 'once';
      state.selectedPlanId = null;
      els.subscribePrice = null;
      els.oncePrice = null;
      els.frequency = null;
      if (requiresPlan) {
        setError('This variant requires a subscription plan.');
      }
      return;
    }

    if (purchaseGroup) purchaseGroup.style.display = '';

    const planMeta = rawPlans.map((plan) => {
      const name = plan.name || '';
      const groupName = plan.groupName || '';
      const combined = `${name} ${groupName}`.trim();
      const nameLower = normalize(combined);
      const isGift = nameLower.includes('gift');
      let cadenceRank = 99;
      if (nameLower.includes('week') && (nameLower.includes('2') || nameLower.includes('two') || nameLower.includes('fortnight'))) {
        cadenceRank = 2;
      } else if (nameLower.includes('week')) {
        cadenceRank = 1;
      } else if (nameLower.includes('month')) {
        cadenceRank = 3;
      }
      return { plan, nameLower, isGift, cadenceRank };
    });

    planMeta.sort((a, b) => {
      if (a.isGift !== b.isGift) return a.isGift ? 1 : -1;
      if (a.cadenceRank !== b.cadenceRank) return a.cadenceRank - b.cadenceRank;
      return (a.plan.name || '').localeCompare(b.plan.name || '');
    });

    giftPlanIds = new Set(planMeta.filter((item) => item.isGift).map((item) => item.plan.id));

    const sellingPlans = planMeta.map((item) => item.plan);
    const preferredPlan =
      planMeta.find((item) => !item.isGift && item.cadenceRank === 1)?.plan ||
      planMeta.find((item) => !item.isGift)?.plan ||
      planMeta[0]?.plan ||
      null;

    if (!state.selectedPlanId || !sellingPlans.find((plan) => plan.id === state.selectedPlanId)) {
      state.selectedPlanId = preferredPlan?.id || null;
    }

    const planOptions = sellingPlans
      .map((plan) => {
        const name = plan.name || '';
        const groupName = plan.groupName || '';
        const combined = `${name} ${groupName}`.trim();
        const nameLower = normalize(combined);
        const isGift = nameLower.includes('gift');

        let label = name || groupName || 'Delivery';
        if (nameLower.includes('week') && (nameLower.includes('2') || nameLower.includes('two') || nameLower.includes('fortnight'))) {
          label = 'Fortnightly delivery';
        } else if (nameLower.includes('week')) {
          label = 'Weekly delivery';
        } else if (nameLower.includes('month')) {
          label = 'Monthly delivery';
        }

        if (isGift) {
          label = `${label} (gift subscription)`;
        }

        return `<option value="${plan.id}"${plan.id === state.selectedPlanId ? ' selected' : ''}>${label}</option>`;
      })
      .join('');

    const subscribeSelected = requiresPlan ? true : state.purchase === 'subscribe';
    state.purchase = subscribeSelected ? 'subscribe' : 'once';

    els.purchase.innerHTML = `
      <div class="wb-quick-add__purchase-option ${subscribeSelected ? 'is-selected' : ''}" data-purchase="subscribe" role="radio" aria-checked="${subscribeSelected}" tabindex="0">
        <span class="wb-quick-add__dot"></span>
        <div class="wb-quick-add__purchase-meta">
          <div class="wb-quick-add__purchase-row">
            <div class="wb-quick-add__purchase-title">Subscribe & save</div>
            <div class="wb-quick-add__purchase-price" data-wb-subscribe-price></div>
          </div>
          <div class="wb-quick-add__purchase-note">Skip or cancel anytime</div>
          <div class="wb-quick-add__purchase-row wb-quick-add__purchase-row--controls">
            <select class="wb-quick-add__frequency" data-wb-frequency ${subscribeSelected ? '' : 'disabled'}>
              ${planOptions}
            </select>
          </div>
          <div class="wb-quick-add__purchase-note wb-quick-add__purchase-note--detail" data-wb-subscribe-note hidden></div>
        </div>
      </div>
      <div class="wb-quick-add__purchase-option ${!subscribeSelected ? 'is-selected' : ''}" data-purchase="once" role="radio" aria-checked="${!subscribeSelected}" tabindex="0">
        <span class="wb-quick-add__dot"></span>
        <div class="wb-quick-add__purchase-meta">
          <div class="wb-quick-add__purchase-row">
            <div class="wb-quick-add__purchase-title">One-time purchase</div>
            <div class="wb-quick-add__purchase-price" data-wb-once-price></div>
          </div>
        </div>
      </div>
    `;

    els.subscribePrice = els.purchase.querySelector('[data-wb-subscribe-price]');
    els.oncePrice = els.purchase.querySelector('[data-wb-once-price]');
    els.frequency = els.purchase.querySelector('[data-wb-frequency]');
    els.purchase.setAttribute('role', 'radiogroup');

    syncSubscribeNote(state.productHandle);

    if (requiresPlan) {
      const onceOption = els.purchase.querySelector('[data-purchase="once"]');
      onceOption.classList.add('is-disabled');
      onceOption.setAttribute('aria-disabled', 'true');
      onceOption.setAttribute('tabindex', '-1');
    }
  };

  const isGiftPlanSelected = () => {
    if (!state.selectedPlanId) return false;
    return giftPlanIds.has(state.selectedPlanId);
  };

  const updateSubscribeNoteVisibility = () => {
    const noteEl = drawer.querySelector('[data-wb-subscribe-note]');
    if (!noteEl) return;
    if (state.purchase !== 'subscribe' || isGiftPlanSelected()) {
      noteEl.hidden = true;
      return;
    }
    if (noteEl.textContent.trim()) {
      noteEl.hidden = false;
    }
  };

  const getSellingPlanPrice = () => {
    if (!state.variant || !state.selectedPlanId) return null;
    const allocation = state.variant.selling_plan_allocations
      ? state.variant.selling_plan_allocations.find((item) => item.selling_plan_id === state.selectedPlanId)
      : null;
    if (allocation && allocation.price) return allocation.price;
    if (allocation && allocation.per_delivery_price) return allocation.per_delivery_price;
    return null;
  };

  const updatePrices = () => {
    if (!state.variant) {
      els.price.textContent = '—';
      els.subtotal.textContent = '—';
      els.submit.disabled = true;
      els.submit.textContent = 'Unavailable';
      return;
    }
    const basePrice = state.variant.price;
    const planPrice = getSellingPlanPrice();
    const subscribePrice = planPrice || basePrice;

    if (els.subscribePrice) {
      els.subscribePrice.textContent = moneyFormat(subscribePrice);
    }
    if (els.oncePrice) {
      els.oncePrice.textContent = moneyFormat(basePrice);
    }

    const unitPrice = state.purchase === 'subscribe' ? subscribePrice : basePrice;
    els.subtotal.textContent = moneyFormat(unitPrice * state.qty);
    els.price.textContent = moneyFormat(basePrice);

    const soldOut = !state.variant.available;
    els.submit.disabled = soldOut;
    els.submit.textContent = soldOut ? 'Sold out' : 'Add to cart';
  };

  const updateVariantFromSelection = (selectedIndex = null) => {
    const product = state.product;
    if (!product) return;

    let variant = null;
    if (selectedIndex === null) {
      variant = findVariantMatch(state.selectedOptions);
    } else {
      variant = resolveVariantAfterSelection(selectedIndex);
    }

    if (variant) {
      state.variant = variant;
      state.selectedOptions = product.options.map((_, idx) => variant[`option${idx + 1}`]);
      clearError();
    } else {
      state.variant = null;
      setError('That combination is unavailable.');
    }

    buildPurchase();
    updateOptionButtons();
    updatePrices();
  };

  const openDrawer = async (trigger) => {
    state.trigger = trigger;
    const handle = trigger.dataset.wbProductHandle;
    if (!handle) return;
    state.productHandle = handle;

    drawer.classList.add('is-open');
    overlay.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    document.body.classList.add('overflow-hidden');
    document.body.classList.add('wb-quick-add-open');
    if (typeof trapFocus === 'function') {
      const focusTarget = drawer.querySelector('[data-wb-submit]') || drawer.querySelector('[data-wb-close]') || drawer;
      trapFocus(drawer, focusTarget);
    }

    clearError();
    const title = trigger.dataset.wbProductTitle || '';
    const image = trigger.dataset.wbProductImage || '';
    const url = trigger.dataset.wbProductUrl || '#';

    if (title) els.title.textContent = title;
    if (image) {
      els.image.innerHTML = `<img src="${image}" alt="${title}" loading="lazy">`;
    } else {
      els.image.textContent = 'Image';
    }
    els.details.href = url;

    try {
      const product = await getProductJson(handle);
      state.product = product;
      state.purchase = 'subscribe';
      state.selectedPlanId = null;
      const defaultVariant = getDefaultVariant(product);
      state.variant = defaultVariant;
      state.selectedOptions = product.options.map((_, idx) => defaultVariant[`option${idx + 1}`]);
      state.qty = 1;
      els.qty.textContent = '1';

      buildOptions();
      buildPurchase();
      updateOptionButtons();
      updatePrices();
    } catch (error) {
      setError('Unable to load product options.');
      state.variant = null;
      updatePrices();
    }
  };

  const closeDrawer = () => {
    drawer.classList.remove('is-open');
    overlay.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('overflow-hidden');
    document.body.classList.remove('wb-quick-add-open');
    if (typeof removeTrapFocus === 'function') {
      removeTrapFocus(state.trigger);
    } else if (state.trigger && typeof state.trigger.focus === 'function') {
      state.trigger.focus();
    }
  };

  const extractSealNoteText = (root) => {
    if (!root) return '';
    const selector = [
      '[data-seal-subscribe]',
      '.sealsubs-target-element',
      '.sls-container',
      '.sls-widget',
      '.seal-subscribe',
      '.seal-subscribe-widget',
      '[class*="sls-"]',
      '[class*="seal-"]',
    ].join(',');
    const rootNode = root.querySelector(selector);
    if (!rootNode) return '';

    const nodes = Array.from(rootNode.querySelectorAll('*'));
    const blocked = new Set(['SELECT', 'OPTION', 'LABEL', 'INPUT', 'SCRIPT', 'STYLE']);
    const MAX_NOTE_LENGTH = 220;

    const matches = nodes
      .filter((el) => el && !blocked.has(el.tagName))
      .map((el) => (el.textContent || '').replace(/\s+/g, ' ').trim())
      .filter((text) => text.length >= 20 && text.length <= MAX_NOTE_LENGTH)
      .filter((text) => /%|off|discount/i.test(text) && /(subscription|subscribe|subscriptions|delivery|order|stackable)/i.test(text));

    if (!matches.length) return '';
    matches.sort((a, b) => b.length - a.length);
    return matches[0];
  };

  const syncSubscribeNote = async (handle) => {
    const noteEl = drawer.querySelector('[data-wb-subscribe-note]');
    if (!noteEl) return;

    noteEl.textContent = '';
    noteEl.hidden = true;

    if (!handle) {
      if (FALLBACK_SUBSCRIBE_NOTE) {
        noteEl.textContent = FALLBACK_SUBSCRIBE_NOTE;
        noteEl.hidden = false;
        updateSubscribeNoteVisibility();
      }
      return;
    }
    if (sealNoteCache.has(handle)) {
      const cached = sealNoteCache.get(handle) || '';
      if (cached) {
        noteEl.textContent = cached;
        noteEl.hidden = false;
        updateSubscribeNoteVisibility();
      }
      return;
    }

    let noteText = extractSealNoteText(document);

    if (!noteText) {
      try {
        const response = await fetch(`/products/${handle}`);
        if (response.ok) {
          const html = await response.text();
          const parsed = new DOMParser().parseFromString(html, 'text/html');
          noteText = extractSealNoteText(parsed);
        }
      } catch (error) {
        noteText = '';
      }
    }

    if (!noteText && FALLBACK_SUBSCRIBE_NOTE) {
      noteText = FALLBACK_SUBSCRIBE_NOTE;
    }

    sealNoteCache.set(handle, noteText || '');

    if (noteText) {
      noteEl.textContent = noteText;
      noteEl.hidden = false;
      updateSubscribeNoteVisibility();
    }
  };

  const handleOptionClick = (event) => {
    const button = event.target.closest('.wb-quick-add__option');
    if (!button) return;
    if (button.disabled) return;
    const grid = button.closest('.wb-quick-add__option-grid');
    if (!grid) return;
    const index = Number(grid.dataset.optionIndex);
    state.selectedOptions[index] = button.dataset.value;
    updateVariantFromSelection(index);
  };

  const handlePurchaseClick = (event) => {
    const option = event.target.closest('.wb-quick-add__purchase-option');
    if (!option) return;
    if (option.classList.contains('is-disabled')) return;

    const purchase = option.dataset.purchase;
    state.purchase = purchase;

    els.purchase.querySelectorAll('.wb-quick-add__purchase-option').forEach((el) => {
      const selected = el.dataset.purchase === purchase;
      el.classList.toggle('is-selected', selected);
      el.setAttribute('aria-checked', selected ? 'true' : 'false');
    });

    if (els.frequency) {
      els.frequency.disabled = purchase !== 'subscribe';
    }

    clearError();
    updateSubscribeNoteVisibility();
    updatePrices();
  };

  const handleFrequencyChange = (event) => {
    state.selectedPlanId = Number(event.target.value);
    clearError();
    updateSubscribeNoteVisibility();
    updatePrices();
  };

  const handleQtyClick = (event) => {
    const btn = event.target.closest('button');
    if (!btn) return;
    const action = btn.dataset.wbQty;
    if (!action) return;

    if (action === 'plus') {
      state.qty += 1;
    } else if (action === 'minus') {
      state.qty = Math.max(1, state.qty - 1);
    }
    els.qty.textContent = String(state.qty);
    updatePrices();
  };

  const handleAddToCart = async () => {
    if (!state.variant) return;
    if (els.submit.disabled) return;

    clearError();

    if (state.purchase === 'subscribe' && !state.selectedPlanId) {
      setError('Please choose a delivery frequency.');
      return;
    }

    els.submit.disabled = true;
    els.submit.textContent = 'Adding...';

    try {
      const config = fetchConfig('javascript');
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      delete config.headers['Content-Type'];

      const formData = new FormData();
      formData.append('id', state.variant.id);
      formData.append('quantity', state.qty);

      if (state.purchase === 'subscribe' && state.selectedPlanId) {
        formData.append('selling_plan', state.selectedPlanId);
      }

      const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      if (cart) {
        formData.append(
          'sections',
          cart.getSectionsToRender().map((section) => section.id)
        );
        formData.append('sections_url', window.location.pathname);
        cart.setActiveElement(state.trigger);
      }

      config.body = formData;

      const response = await fetch(window.routes?.cart_add_url || '/cart/add.js', config);
      const parsed = await response.json();

      if (parsed.status) {
        els.error.textContent = parsed.description || parsed.message || 'Unable to add to cart.';
        els.submit.disabled = false;
        els.submit.textContent = 'Add to cart';
        return;
      }

      if (!cart) {
        window.location = window.routes?.cart_url || '/cart';
        return;
      }

      publish(PUB_SUB_EVENTS.cartUpdate, {
        source: 'wb-quick-add',
        productVariantId: state.variant.id,
        cartData: parsed,
      }).then(() => {
        cart.renderContents(parsed);
      });

      closeDrawer();
    } catch (error) {
      console.error(error);
      els.error.textContent = 'Unable to add to cart.';
      els.submit.disabled = false;
      els.submit.textContent = 'Add to cart';
    }
  };

  document.addEventListener(
    'click',
    (event) => {
      const trigger = event.target.closest('[data-wb-quick-add]');
      if (trigger) {
        event.preventDefault();
        event.stopPropagation();
        if (event.stopImmediatePropagation) event.stopImmediatePropagation();
        openDrawer(trigger);
        return;
      }

      if (event.target.closest('[data-wb-close]')) {
        closeDrawer();
      }
    },
    true
  );

  overlay.addEventListener('click', closeDrawer);

  drawer.addEventListener('click', (event) => {
    if (event.target.closest('[data-wb-close]')) return;
    if (event.target.closest('.wb-quick-add__option')) {
      handleOptionClick(event);
    }
    if (event.target.closest('.wb-quick-add__purchase-option')) {
      handlePurchaseClick(event);
    }
    if (event.target.closest('[data-wb-qty]')) {
      handleQtyClick(event);
    }
  });

  drawer.addEventListener('change', (event) => {
    if (event.target.matches('[data-wb-frequency]')) {
      handleFrequencyChange(event);
    }
  });

  drawer.addEventListener('keydown', (event) => {
    if (
      (event.key === 'Enter' || event.key === ' ') &&
      event.target.closest('.wb-quick-add__purchase-option')
    ) {
      event.preventDefault();
      handlePurchaseClick(event);
    }
  });

  els.submit.addEventListener('click', handleAddToCart);

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') closeDrawer();
  });

  window.WBQuickAddDrawer = {
    openByHandle: async (handle, trigger = null) => {
      const fakeTrigger = trigger || {
        dataset: { wbProductHandle: handle, wbProductTitle: '', wbProductImage: '', wbProductUrl: `/products/${handle}` },
      };
      await openDrawer(fakeTrigger);
    },
  };
})();
